<template>
  <div
    :class="controlClasses"
    class="mds-form-control mds-select"
    @mouseenter="showDisabledHint($event)"
    @mouseleave="hideDisabledHint()"
  >
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          ref="searchInput"
          type="text"
          autocomplete="off"
          :disabled="disabled"
          :id="uid"
          :placeholder="searchInputPlaceholder"
          :value="searchInputValue"
          @input="onSearchInput($event)"
          @focusin="onSearchInputFocusIn"
          @focusout="onSearchInputFocusOut"
        />

        <div v-if="hasBadgeSlot" class="mds-badge">
          <slot name="badge" />
        </div>

        <button
          tabindex="-1"
          @click="onClear"
          class="clear"
          :class="{ visible: showClear }"
          ref="clearButton"
          type="button"
        >
          <VIcon material="cancel" />
        </button>

        <div class="triangle" @click="onTriangleClick">
          <VIcon material="arrow_drop_down" />
        </div>
      </div>

      <div class="messages-row" v-if="isErrorsVisible">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      :offset="1"
      :target="'#' + uid"
      :visible="isFocused"
      @keydown="keyDownListeners"
      @update:visible="setFocusOut"
      :align="popupMinWidth === null ? 'left' : 'auto'"
      :min-width="popupMinWidth"
      inherit-width
      no-pointer
      position="bottom"
      ref="selectPopup"
      class="mds-select-popup-wrapper"
    >
      <div :class="popupClasses" class="mds-select-popup">
        <div
          v-show="optionsLocalSelected.length > 0 && isSearchable === true"
          class="mds-select-options selected"
        >
          <div
            v-for="option of optionsLocalSelected"
            :key="'selected_' + option.id"
            :class="getOptionClasses(option)"
            @click="onUnselectOption($event, option)"
            class="mds-select-options-item"
          >
            <template v-if="haveOptionSlot">
              <slot name="option" v-bind:option="option" />
            </template>
            <template v-else>
              {{ getOptionLabel(option) }}
              <div v-if="getOptionDescription(option)" class="description">
                {{ getOptionDescription(option) }}
              </div>
            </template>
          </div>
        </div>

        <div
          v-show="optionsLocalCommon.length > 0"
          class="mds-select-options"
          ref="selectOptions"
        >
          <template v-for="option of optionsLocalCommon">
            <div
              v-if="isSearchable === false || (isSearchable === true && !valueObject[option.id])"
              :key="option.id"
              :class="getOptionClasses(option)"
              :id="optionHtmlId(option)"
              @click="onSelectOption(option, $event)"
              @mouseenter="setHoveredOption(option)"
              @mouseleave="unsetHoveredOption(option)"
              class="mds-select-options-item"
            >
              <template v-if="haveOptionSlot">
                <slot name="option" v-bind:option="option" />
              </template>
              <template v-else>
                {{ getOptionLabel(option) }}
                <div v-if="getOptionDescription(option)" class="description">
                  {{ getOptionDescription(option) }}
                </div>
              </template>
            </div>
          </template>
        </div>

        <div
          v-show="optionsLocalCommon.length === 0 && optionsLocalSelected.length === 0"
          class="mds-select-options-empty"
        >
          <div class="loading-icon" v-if="showLoading">
            <i class="fa fa-spinner" />
          </div>

          <template v-else>
            <template v-if="hasEmptyOptionsSlot">
              <!-- @slot Слот вспомогательного текста -->
              <slot name="empty-options" />
            </template>
            <template v-else>
              Ничего не найдено
            </template>
          </template>
        </div>
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VSelect.scss" />

<script lang="ts">
import Core              from "@/core/Core";
import VBaseControl      from "@/core/services/VBaseControl";
import RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";

import VPopup   from "@/lib/components/VPopup/VPopup.vue";
import VIcon    from "@/lib/components/VIcon/VIcon.vue";
import Dictionary from "@/core/types/Dictionary";

/** Задержка поиска после последнего нажатия — при быстром вводе уходит один запрос. */
const SEARCH_DEBOUNCE_MS = 300;

export default defineComponent({
  name: "VSelect",

  components: { VIcon, VPopup },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** Минимальная ширина попапа в px, если ширина контрола мала (фильтры в гриде) */
    popupMinWidth: {
      type:    Number,
      default: null,
    },
    /** Массив объектов или Map для селекта, может быть пустым при использовании dataUrl */
    options: {
      type:    [Array, Map] as PropType<unknown[] | Map<unknown, unknown> | Dictionary>,
      default: () => [],
    },
    /** Опции, всегда отображаемые поверх результатов поиска */
    optionsNested: {
      type:    Array,
      default: () => [],
    },
    /** Если true — optionsNested добавляются перед результатами поиска, иначе после */
    optionsNestedPrepend: {
      type:    Boolean,
      default: false,
    },
    /** Имя свойства объекта для отображения */
    optionLabel: {
      type:    String,
      default: "name",
    },
    /** Имя свойства объекта-идентификатора */
    optionIdentity: {
      type:    String,
      default: "id",
    },
    /** Имя свойства объекта для подсказки опции (серым текстом под лейблом) */
    optionDescription: {
      type:    String,
      default: null,
    },
    /** Валидировать options и текущее значение */
    validateOptions: {
      type:    Boolean,
      default: false,
    },
    /** v-model. Значение — объект (single) или массив объектов (multiple). */
    modelValue: {
      type:    [Array, Object, null],
      default: null,
    },
    /** Обратная совместимость с :value */
    value: {
      type:    [Array, Object, null],
      default: null,
    },
    /** Множественный выбор */
    multiple: {
      type:    Boolean,
      default: false,
    },
    /** Включает поиск по вариантам */
    searchable: {
      type:    Boolean,
      default: false,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: true,
    },
    /** Внешняя функция поиска */
    searchFunction: {
      type:    Function,
      default: null,
    },
    /** Endpoint для загрузки вариантов */
    dataUrl: {
      type:    String,
      default: null,
    },
    /** Имя параметра запроса с поисковой строкой */
    dataUrlParameterName: {
      type:    String,
      default: "pattern",
    },
    /** Мин. длина ввода для отправки запроса */
    minimumInputLength: {
      type:    Number,
      default: 1,
    },
    /** Макс. длина ввода (остальное обрежется) */
    maximumInputLength: {
      type:    Number,
      default: 50,
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => ["left", "center", "right"].includes(value),
    },
    /** Индикатор загрузки опций */
    loading: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'input', 'focus', 'blur'],

  data() {
    return {
      isFocused:      false,
      optionsLocal:   [],
      valueObject:    {},
      loadingOptions: false,
      searchValue:    null,
      hoveredOption:  null,
      loadingLocal:   false,
      // Таймер отложенного поиска (дебаунс ввода)
      searchTimer:    null,
    };
  },

  computed: {
    /** Приоритет modelValue, потом value (обратная совместимость). */
    currentValue() {
      if (this.modelValue !== undefined && this.modelValue !== null) return this.modelValue;
      return this.value;
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    isSearchable(): boolean {
      return this.searchable === true
        || this.dataUrl !== null
        || this.searchFunction !== null
        || this.options.length > 10;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.multiple === true)   classes.push("multiple");
      if (this.isFocused === true)  classes.push("focused");
      if (this.isValueEmpty)        classes.push("empty");
      return classes;
    },

    popupClasses() {
      return {
        "with-search": this.isSearchable,
        multiple:      this.multiple,
      };
    },

    isValueEmpty(): boolean {
      if (!this.currentValue) return true;
      return this.multiple === true
        ? !Array.isArray(this.currentValue) || this.currentValue.length === 0
        : !this.currentValue;
    },

    valueLabel() {
      if (this.isValueEmpty === true) return null;
      if (this.multiple === true) {
        if (this.currentValue.length === 1) {
          return this.resolveOptionLabel(this.currentValue[0]);
        }
        return `Выбрано: ${this.currentValue.length} эл.`;
      }
      return this.resolveOptionLabel(this.currentValue);
    },

    searchInputValue() {
      if (this.isSearchable === true && this.isFocused === true) {
        return this.searchValue;
      }
      return this.valueLabel;
    },

    searchInputPlaceholder() {
      if (this.isSearchable === true && this.isFocused === true) {
        if (this.minimumInputLength !== 0) {
          return `Введите ${this.minimumInputLength} символа для начала поиска`;
        }
        return "Введите для начала поиска";
      }
      return this.placeholder;
    },

    showClear(): boolean {
      return this.disabled === false
        && (
          (this.clearable === true && this.isValueEmpty === false)
          || (this.multiple === true && this.isValueEmpty === false)
        );
    },

    isExternalSearch(): boolean {
      return this.searchFunction !== null || this.dataUrl !== null;
    },

    showLoading(): boolean {
      return this.isExternalSearch === true && this.loadingOptions === true;
    },

    haveOptionSlot(): boolean {
      return this.$slots.option !== undefined;
    },

    optionsLocalSelected() {
      return Object.keys(this.valueObject).map((key) => this.valueObject[key]);
    },

    optionsLocalCommon() {
      if (this.optionsNested.length > 0) {
        const cleanOptions = this.optionsLocal.filter(
          (item) => this.optionsNestedIds.includes(item[this.optionIdentity]) === false
        );
        return this.optionsNestedPrepend === true
          ? [...this.optionsNested, ...cleanOptions]
          : [...cleanOptions, ...this.optionsNested];
      }
      return this.optionsLocal;
    },

    optionsNestedIds() {
      return this.optionsNested.map((item) => item[this.optionIdentity]);
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasBadgeSlot(): boolean {
      return this.$slots.badge !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    hasEmptyOptionsSlot(): boolean {
      return this.$slots["empty-options"] !== undefined;
    },
  },

  watch: {
    options(value) {
      if (this.isExternalSearch === false) {
        this.optionsLocal = value instanceof Map ? [...value.values()] : [...value];
      }
    },
    optionsLocal(value) {
      const popup = this.$refs.selectPopup;
      if (Array.isArray(value) && value.length > 0) {
        this.hoveredOption = this.optionsLocalCommon[0];
        this.calculateOptionsWrapScroll(this.hoveredOption);
      }
      this.$nextTick().then(() => (popup ? popup.calculatePosition() : null));
    },
    searchValue(value) {
      // Ввод продолжается — отложенный поиск по прошлому значению уже не актуален.
      this.cancelPendingSearch();

      if (value === null) return;
      if (value && value.length < this.minimumInputLength) return;

      /**
       * Без задержки:
       * - value === ""                    — строку очистили, сбрасываем список сразу;
       * - isExternalSearch === false      — нет ни dataUrl, ни searchFunction: опции из пропа
       *                                     options фильтруются локально, запроса нет.
       * Иначе — поиск на бэкенде, откладываем: при быстром вводе уходит один запрос.
       */
      if (value === "" || this.isExternalSearch === false) {
        this.search(value);
      } else {
        this.searchTimer = setTimeout(() => {
          this.searchTimer = null;
          this.search(value);
        }, SEARCH_DEBOUNCE_MS);
      }
    },
    loading(value) {
      this.loadingLocal = value;
    },
    currentValue(newVal, oldVal) {
      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validate();
        this.validation.markDirty();
      }
      // Кэш выбранных опций по идентификатору — для быстрого поиска.
      const valueObject: Record<string, unknown> = {};
      if (Array.isArray(newVal)) {
        newVal.forEach((option) => (valueObject[option[this.optionIdentity]] = option));
      } else {
        if (newVal !== null && typeof newVal === "object") {
          valueObject[newVal[this.optionIdentity]] = newVal;
        }
      }
      this.valueObject = valueObject;
    },
  },

  created() {
    if (this.isExternalSearch === false) {
      this.optionsLocal = this.options instanceof Map
        ? [...this.options.values()]
        : [...this.options];
    }
    if (this.multiple === true && Array.isArray(this.currentValue) === false) {
      this.$emit("update:modelValue", []);
      this.$emit("input", []);
    }
  },

  beforeUnmount() {
    // Контрол уходит — отложенный поиск не должен выстрелить после размонтирования.
    this.cancelPendingSearch();
  },

  methods: {
    /** Сравнивает две опции по идентификатору */
    isSameOption(optionOne, optionTwo) {
      return optionOne[this.optionIdentity] === optionTwo[this.optionIdentity];
    },

    /** Проверяет выбрана ли опция */
    isOptionSelected(option) {
      if (!this.currentValue) return false;
      if (this.multiple === true) {
        if (Array.isArray(this.currentValue) === false) {
          console.warn('[Select] currentValue should be array in multiple mode');
          return false;
        }
        return this.currentValue.some(val => this.isSameOption(val, option));
      }
      return this.isSameOption(this.currentValue, option);
    },

    getOptionClasses(option) {
      const result: Record<string, boolean> = {};
      if (this.isOptionSelected(option)) result.selected = true;
      if (this.hoveredOption !== null && this.isSameOption(option, this.hoveredOption)) {
        result.hovered = true;
      }
      return result;
    },

    doFocus() {
      this.$refs.searchInput.focus();
    },

    /**
     * Формирует отображение опции с учётом особых случаев.
     * Актуален для фильтрации позиций заказа по артикулам товара под поставщиком —
     * когда название товара покупателя не должно быть видно поставщику.
     */
    resolveOptionLabel(option) {
      switch (this.optionLabel) {
        case 'toString':
          return option?.toString() ?? '';
        case 'category':
          return option?.category ? `${option.category.name} ${option.code ?? ""}` : "";
        default:
          return option?.[this.optionLabel] ?? "";
      }
    },

    getOptionLabel(option) {
      return this.resolveOptionLabel(option);
    },

    getOptionDescription(option) {
      if (this.optionDescription === null) return null;
      return option[this.optionDescription];
    },

    optionHtmlId(option) {
      const identity = option?.[this.optionIdentity];
      if (identity === null || identity === undefined) return `option_${this.uid}_unknown`;
      return `option_${this.uid}_${String(identity).replace(/\s/, "_")}`;
    },

    onSearchInputFocusIn(event) {
      if (this.disabled === true) return;
      if (this.isFocused === false) {
        this.setFocusIn(event);
      }
    },

    onSearchInputFocusOut(event) {
      if (
        event instanceof FocusEvent
        && event.relatedTarget !== null
        && event.relatedTarget.classList.contains("mds-select-options") === false
      ) {
        if (this.disabled === true) return;
        if (this.isFocused === true) {
          this.setFocusOut();
        }
      }
    },

    setFocusIn(event) {
      this.validation.markTouched();

      if (this.$refs.clearButton.contains(event.target) === false) {
        this.hoveredOption = this.optionsLocalCommon.length > 0
          ? this.optionsLocalCommon[0]
          : null;

        if (this.hoveredOption !== null) {
          this.calculateOptionsWrapScroll(this.hoveredOption);
        }

        /**
         * Хитрый хак — важен при программном фокусе (например, навигация по Enter).
         *
         * Enter, нажатый для перехода на текущий VSelect, воспринимается как команда
         * самим контролом — нужно отложить установку обработчиков на следующий тик,
         * чтобы исходное событие не задело наше состояние.
         */
        setTimeout(() => {
          this.isFocused = true;
          this.$emit("focus");
          window.addEventListener("keydown", this.keyDownListeners);

          // Загружаем опции при фокусе для внешнего поиска без обязательного ввода.
          if (this.isExternalSearch === true && this.minimumInputLength === 0) {
            this.search(null);
          }
        }, 0);
      }
    },

    setFocusOut() {
      // Список закрыт — отложенный поиск больше не нужен.
      this.cancelPendingSearch();

      const wrap = this.$refs.selectOptions;
      if (wrap) wrap.scrollTop = 0;
      this.isFocused     = false;
      this.searchValue   = null;
      this.hoveredOption = null;

      window.removeEventListener("keydown", this.keyDownListeners);

      const searchInput = this.$refs.searchInput;
      if (searchInput && typeof searchInput.blur === "function") {
        searchInput.blur();
      }

      this.$emit("blur", this.currentValue);
    },

    keyDownListeners(event) {
      if (event.target.tagName !== "INPUT" || event.key === "Enter") {
        event.preventDefault();
      }

      switch (event.key) {
        case "ArrowDown":
          if (this.hoveredOption !== null) {
            const index = this.optionsLocalCommon.findIndex(
              (option) => option[this.optionIdentity] === this.hoveredOption[this.optionIdentity]
            );
            if (index > -1 && index < this.optionsLocalCommon.length - 1) {
              this.hoveredOption = this.optionsLocalCommon[index + 1];
            } else if (index === this.optionsLocalCommon.length - 1) {
              this.hoveredOption = this.optionsLocalCommon[0];
            }
          } else {
            this.hoveredOption = this.optionsLocalCommon[0];
          }
          this.calculateOptionsWrapScroll(this.hoveredOption, event.key);
          break;

        case "ArrowUp":
          if (this.hoveredOption !== null) {
            const index = this.optionsLocalCommon.findIndex(
              (option) => option[this.optionIdentity] === this.hoveredOption[this.optionIdentity]
            );
            if (index > 0 && index < this.optionsLocalCommon.length) {
              this.hoveredOption = this.optionsLocalCommon[index - 1];
            } else if (index === 0) {
              this.hoveredOption = this.optionsLocalCommon[this.optionsLocalCommon.length - 1];
            }
          } else {
            this.hoveredOption = this.optionsLocalCommon[this.optionsLocalCommon.length - 1];
          }
          this.calculateOptionsWrapScroll(this.hoveredOption, event.key);
          break;

        case "Enter":
          this.onSelectOption(this.hoveredOption);
          break;

        case "Escape":
          this.setFocusOut();
          break;
      }
    },

    calculateOptionsWrapScroll(option, keyName) {
      const wrap = this.$refs.selectOptions;

      if (wrap && option) {
        const element = wrap.querySelector(`#${this.optionHtmlId(option)}`);
        if (element === null) return;

        switch (keyName) {
          case "ArrowUp":
            if (
              wrap.scrollTop > element.offsetTop
              || element.offsetTop > wrap.scrollTop + wrap.offsetHeight
            ) {
              wrap.scrollTop = element.offsetTop;
            }
            break;
          case "ArrowDown":
            if (
              element.offsetTop + element.offsetHeight > wrap.offsetHeight + wrap.scrollTop
              || wrap.scrollTop > element.offsetTop + element.offsetHeight
            ) {
              wrap.scrollTop = element.offsetTop + element.offsetHeight - wrap.offsetHeight;
            } else if (element.offsetTop === 0) {
              wrap.scrollTop = 0;
            }
            break;
          default:
            if (element.offsetTop + element.offsetHeight < wrap.scrollTop) {
              wrap.scrollTop = element.offsetTop;
            } else if (wrap.offsetHeight + wrap.scrollTop > element.offsetTop) {
              wrap.scrollTop = element.offsetTop + element.offsetHeight - wrap.offsetHeight;
            }
            break;
        }
      }
    },

    setHoveredOption(option) {
      this.hoveredOption = option;
    },

    unsetHoveredOption(option) {
      if (
        option !== null
        && this.hoveredOption !== null
        && this.hoveredOption[this.optionIdentity] === option[this.optionIdentity]
      ) {
        this.hoveredOption = null;
      }
    },

    onSelectOption(option, event) {
      if (event && event instanceof MouseEvent) {
        event.stopPropagation();
      }

      if (this.multiple === false && this.currentValue && this.isSameOption(this.currentValue, option)) {
        this.setFocusOut();
        return;
      }

      let value = null;

      if (this.multiple === true) {
        value = Array.isArray(this.currentValue) && this.currentValue.length > 0
          ? [...this.currentValue]
          : [];
        if (this.isSearchable === true) {
          value.push(option);
        } else {
          const optionIndex = value.findIndex(
            (v) => v[this.optionIdentity] === option[this.optionIdentity]
          );
          if (optionIndex === -1) {
            value.push(option);
          } else {
            value.splice(optionIndex, 1);
          }
        }
      } else {
        value = option;
      }

      if (this.multiple === true) {
        const optionsLength = this.optionsLocalCommon.length;
        if (optionsLength > 1) {
          const optionIndex = this.optionsLocalCommon.findIndex(
            (item) => item[this.optionIdentity] === option[this.optionIdentity]
          );
          if (optionIndex === 0) {
            this.hoveredOption = this.optionsLocalCommon[1];
          } else if (optionIndex > 0 && optionIndex <= optionsLength - 1) {
            this.hoveredOption = this.optionsLocalCommon[optionIndex - 1];
          }
        }
      } else {
        this.setFocusOut();
      }

      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },

    onUnselectOption(event, option) {
      if (event && event instanceof MouseEvent) {
        event.stopPropagation();
      }

      if (this.multiple === false) {
        this.setFocusOut();
        return;
      }

      let value = Array.isArray(this.currentValue) && this.currentValue.length > 0
        ? [...this.currentValue]
        : [];
      const optionIndex = value.findIndex(
        (v) => v[this.optionIdentity] === option[this.optionIdentity]
      );
      if (optionIndex > -1) {
        value.splice(optionIndex, 1);
      }

      if (this.multiple === false && value.length === 0) {
        value = null;
      }

      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },

    onClear() {
      const value = this.multiple === true ? [] : null;
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
      this.$emit("blur", value);
    },

    onSearchInput(event) {
      let value = event.target.value;
      if (this.isSearchable === false) return;

      if (typeof value === "string" && value.length > this.maximumInputLength) {
        value = value.slice(0, this.maximumInputLength);
        event.target.value = value;
      }

      this.searchValue = value;
    },

    /** Снимает отложенный поиск, если он ещё не выстрелил. */
    cancelPendingSearch() {
      if (this.searchTimer !== null) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      } else {
        // отложенного поиска нет — снимать нечего
      }
    },

    defaultSearchFunction(query) {
      const re = new RegExp(query, "i");
      return this.options.filter((option) => {
        const label = typeof option[this.optionLabel] === 'function'
          ? option[this.optionLabel]()
          : option[this.optionLabel];
        return re.test(label);
      });
    },

    /**
     * Поиск опций.
     *
     * Для dataUrl используется abortPrevious — при новом запросе предыдущий отменяется автоматически.
     * При очистке поисковой строки предыдущий запрос отменяется вручную через Core.rpc.abortByMethod.
     * Пагинация ограничивает выдачу (RpcDataPagination.createDefault()), чтобы не тянуть тысячи записей.
     */
    async search(query) {
      // Пустой запрос — сброс к исходному состоянию (кроме случая, когда разрешён поиск без ввода).
      // Поиск без ввода полезен, чтобы не загружать опции отдельным методом.
      if (!query && this.minimumInputLength !== 0) {
        // Локальные опции — просто восстанавливаем полный список.
        if (this.isExternalSearch === false) {
          this.optionsLocal = [...this.options];
          return;
        }

        // Внешний поиск — отменяем летящий запрос, если есть.
        if (this.dataUrl !== null) {
          Core.rpc.abortByMethod(this.dataUrl);
        }

        // Оставляем в списке только текущее выбранное значение.
        const value = [];
        if (Array.isArray(this.currentValue)) {
          value.push(...this.currentValue);
        } else if (this.currentValue) {
          value.push(this.currentValue);
        }
        this.optionsLocal = value;
        this.loadingLocal = false;
        return;
      }

      // Поиск по dataUrl — запрос на бэкенд.
      if (this.dataUrl !== null) {
        this.loadingLocal = true;

        const response = await Core.rpc.makeRequestSilent(
          this.dataUrl,
          { [this.dataUrlParameterName]: query || null },
          RpcDataPagination.createDefault(),
          null,
          false,
          { abortPrevious: true },
        );

        this.loadingLocal = false;

        // Запрос был отменён следующим вводом — результат уже не актуален.
        if (response.isCancelled) return;

        if (response.ok) {
          this.setSearchResults(response.data);
        } else {
          this.setSearchResults([]);
        }
      }
      // Поиск через внешнюю функцию (из проп searchFunction).
      else if (typeof this.searchFunction === "function") {
        const data = this.searchFunction(query);

        // searchFunction может вернуть Promise (асинхронный поиск) или массив.
        if (data instanceof Promise) {
          this.loadingLocal = true;
          try {
            const options = await data;
            // DAO.getList возвращает { data, meta } — достаём data.
            this.setSearchResults(options?.data || options);
          } catch {
            this.setSearchResults([]);
          } finally {
            this.loadingLocal = false;
          }
        } else {
          this.setSearchResults(data);
        }
      }
      // Фолбэк — фильтрация по локальным опциям через регулярку.
      else {
        const data = this.defaultSearchFunction(query);
        this.setSearchResults(data);
      }
    },

    setSearchResults(options) {
      const value = [];
      if (Array.isArray(this.currentValue)) {
        value.push(...this.currentValue);
      } else if (this.currentValue) {
        value.push(this.currentValue);
      }

      // Убираем из результатов опции, которые уже выбраны, чтобы не было дублей.
      const selectedIds = new Set(value.map(v => v[this.optionIdentity]));
      const filtered    = options.filter(option => selectedIds.has(option[this.optionIdentity]) === false);

      this.optionsLocal = [...value, ...filtered];
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.currentValue);
    },

    onTriangleClick(event) {
      event.stopPropagation();
      const input = this.$el.querySelector("#" + this.uid);
      if (input && typeof input.focus === "function") {
        input.focus();
      }
    },
  },
});
</script>